import { useMemo } from 'react'

import {
  matchFilterBetweenDates, matchFilterKey, matchFilterNumber, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useDebounce, useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import formatDate from '@functions/formatDate'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ActivityModel, ActivityRequestOptions } from '@models/types'

const watchEntityKeys = ['activities']

export type ActivityFilters = {
  // Also match records belonging to the campaign (recipient_type must be Campaign)
  campaign_records?: boolean,
  created_date_end?: string,
  created_date_start?: string,
  key?: string,
  // Comma separated list of key prefixes e.g. 'invoice.,credit_note.'
  key_prefixes?: string,
  limit?: number,
  recipient_id?: number,
  recipient_type?: string,
  trackable_only?: boolean,
}

type UseActivitiesOptions = {
  filters?: ActivityFilters,
  performHttpRequests?: boolean,
  // Change this value to force the activities to be reloaded
  refetchKey?: number | string,
  requestOptions?: ActivityRequestOptions,
}

function useActivities(options: UseActivitiesOptions = {}) {
  const { filters = {}, refetchKey, requestOptions } = options
  const {
    campaign_records: filterCampaignRecords,
    created_date_end: filterCreatedDateEnd,
    created_date_start: filterCreatedDateStart,
    key: filterKey,
    key_prefixes: filterKeyPrefixes,
    limit: filterLimit,
    recipient_id: filterRecipientId,
    recipient_type: filterRecipientType,
    trackable_only: filterTrackableOnly,
  } = filters

  const {
    updatedEntities: { activities: activitiesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  // Wait for a burst of related updates (e.g. credit note then its items) to settle before reloading
  const debouncedRefetchKey = useDebounce(refetchKey, 600)

  const { activities } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredActivities = useMemo(() => {
    const filtered = Object.values(activities).filter((activity: ActivityModel) => {
      const {
        dates, key, recipient_id, recipient_type, trackable_id, trackable_type,
      } = activity

      const createdAt = formatDate(dates?.created?.date_time, 'dd/MM/yyyy HH:mm')

      const matchRecipientId = matchFilterNumber(recipient_id, filterRecipientId)
      const matchRecipientType = matchFilterKey(recipient_type, filterRecipientType)
      // Activities logged against the campaign's records instead of the campaign carry the campaign_id
      const matchCampaignRecord = !!filterCampaignRecords
        && (activity as ActivityModel & { campaign_id?: number }).campaign_id === filterRecipientId
      const matchRecipient = (matchRecipientId && matchRecipientType) || matchCampaignRecord

      // Backend matches against recipient or trackable
      const matchTrackableId = matchFilterNumber(trackable_id, filterRecipientId)
      const matchTrackableType = matchFilterKey(trackable_type, filterRecipientType)
      const matchTrackable = matchTrackableId && matchTrackableType

      // Match key
      const matchKey = matchFilterKey(key, filterKey)
        && (!filterKeyPrefixes || filterKeyPrefixes.split(',').some(prefix => key?.startsWith(prefix.trim())))

      // Match dates
      const matchCreatedAt = matchFilterBetweenDates(createdAt, filterCreatedDateStart, filterCreatedDateEnd)

      if (filterTrackableOnly){
        // when this is true we match only activities relating to the specific entity,
        // not the parent and the siblings of the entity
        return matchTrackable && matchCreatedAt && matchKey
      }
      return (matchRecipient || matchTrackable) && matchCreatedAt && matchKey
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [activitiesUpdatedAt, JSON.stringify(filters)])

  const filteredActivitiesCount = filteredActivities.length
  const hasFilteredActivities = !!filteredActivitiesCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredActivitiesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const { loading: loadingActivities } = useReduxAction(
    'activities',
    'loadActivities',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests, debouncedRefetchKey],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && (refetchKey !== undefined || !loading),
      // The load key doesn't include refetchKey, so skip the "already loaded" check to allow reloading
      skipLoadedForKeysCheck: refetchKey !== undefined,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredActivities,
    filteredActivitiesCount,
    hasFilteredActivities,
    loading: loadingActivities,
  }
}

export type UseActivitiesPayload = ReturnType<typeof useActivities>

export default useActivities
